import { memo, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import type { QuickChartData } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis';
import { KPIWidget as KPIWidgetUI } from '@trinity/chat-components/components/QuantAnalysis/KPIWidget';
import { NivoBarChart } from '@trinity/chat-components/components/Widgets/Charts/Bar';
import { CompactChartLegend } from '@trinity/chat-components/components/Widgets/Charts/CompactLegend';
import { NivoLineChart } from '@trinity/chat-components/components/Widgets/Charts/Line';
import { NivoMultipleBarChart } from '@trinity/chat-components/components/Widgets/Charts/MultipleBar';
import { NivoPieChart } from '@trinity/chat-components/components/Widgets/Charts/Pie';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Widgets/Charts/StackedBar';
import { ProgressBarWidget } from '@trinity/chat-components/components/Widgets/ProgressBarWidget';
import { SemiCircleProgressWidget } from '@trinity/chat-components/components/Widgets/SemiCircleProgressWidget';
import { DEFAULT_COLORS } from '@trinity/chat-components/lib/chart-colors';

function renderKPIWidget(chartData: QuickChartData & { type: 'kpi-widget' }) {
  return (
    <KPIWidgetUI
      title={chartData.title ?? ''}
      backgroundColor="transparent"
      values={chartData.values ?? []}
      labels={chartData.labels ?? []}
    />
  );
}

function renderProgressBar(chartData: QuickChartData & { type: 'progress-bar-widget' }, colors: string[]) {
  return (
    <ProgressBarWidget
      title={chartData.title ?? ''}
      backgroundColor="transparent"
      values={chartData.values ?? []}
      minValues={chartData.minValues ?? []}
      maxValues={chartData.maxValues ?? []}
      labels={chartData.labels ?? []}
      colors={chartData.colors ?? colors}
    />
  );
}

function renderSemiCircleProgress(chartData: QuickChartData & { type: 'semi-circle-progress-widget' }, colors: string[]) {
  return (
    <SemiCircleProgressWidget
      title={chartData.title ?? ''}
      backgroundColor="transparent"
      values={chartData.values ?? []}
      minValues={chartData.minValues ?? []}
      maxValues={chartData.maxValues ?? []}
      labels={chartData.labels ?? []}
      colors={chartData.colors ?? colors}
      units={chartData.units}
      insights={chartData.insights}
    />
  );
}

function renderDataTable(chartData: QuickChartData & { type: 'data-table' }, colors: string[]) {
  const tableData = {
    columnNames: chartData.customColumnNames ?? chartData.data.columnNames,
    rowNames: chartData.customRowNames ?? chartData.data.rowNames,
    records: chartData.data.records,
    metadata: chartData.data.metadata,
  };
  return (
    <DataTableContainer>
      <AnalysisTable
        data={tableData}
        headerBackgroundColor={chartData.headerColor ?? colors[0]}
        initialSortConfig={chartData.sortConfig}
        disableSorting={false}
        disableSelection={true}
      />
    </DataTableContainer>
  );
}

function renderMultipleBar(chartData: QuickChartData & { type: 'multiple-clustered-bar' | 'multiple-stacked-bar' }, colors: string[]) {
  return (
    <NivoMultipleBarChart
      chart={chartData.chart}
      colors={colors}
      isStacked={chartData.type === 'multiple-stacked-bar'}
      unit={chartData.options?.unit}
      isPrefix={chartData.options?.isPrefix}
      seriesToN={chartData.options?.seriesToN}
    />
  );
}

function renderSimpleBarOrPie(chartData: Extract<QuickChartData, { type: 'bar' | 'stacked-bar' | 'pie' }>, colors: string[]) {
  const unit = chartData.options?.unit;
  const isPrefix = chartData.options?.isPrefix;
  switch (chartData.type) {
    case 'bar':
      return <NivoBarChart chart={chartData.chart} colors={colors} unit={unit} isPrefix={isPrefix} />;
    case 'stacked-bar':
      return <NivoStackedBarChart chart={chartData.chart} colors={colors} unit={unit} isPrefix={isPrefix} />;
    case 'pie':
      return <NivoPieChart chart={chartData.chart} colors={colors} unit={unit} isPrefix={isPrefix} />;
    default:
      return null;
  }
}

/**
 * ChartVisualizationUI — pure presentation component for chart widgets.
 *
 * All data is received via props. Uses `colors` prop instead of the
 * app-local `useOptionsColors` hook, so it is reusable across packages.
 */

type Props = {
  /** The chart data shape (discriminated union by chart type) */
  chartData: QuickChartData;
};

export const ChartVisualizationUI = memo(function ChartVisualizationUI({
  chartData,
}: Props): React.ReactElement | null {
  // Use colors embedded in the chart data (set by HTTP agents), falling back to the default palette.
  const chartDataColors = 'colors' in chartData ? (chartData as { colors?: string[] }).colors : undefined;
  const colors = chartDataColors ?? DEFAULT_COLORS;
  const [legendOpen, setLegendOpen] = useState(false);

  const legend = useMemo(() => {
    const legendData: { key: string; color: string; label?: string }[] = [];

    switch (chartData.type) {
      case 'bar':
      case 'stacked-bar': {
        const data = chartData.chart.data;
        for (let i = 0; i < data.length; i++) {
          legendData.push({ key: data[i].x, color: colors[i % colors.length] });
        }
        break;
      }
      case 'pie': {
        const data = chartData.chart.data;
        for (let i = 0; i < data.length; i++) {
          legendData.push({ key: data[i].label, color: colors[i % colors.length] });
        }
        break;
      }
      case 'multiple-clustered-bar':
      case 'multiple-stacked-bar': {
        const keysSet = new Set<string>();
        for (const row of chartData.chart.data) {
          for (const point of row.data) {
            keysSet.add(point.x);
          }
        }
        const keys = Array.from(keysSet);
        for (let i = 0; i < keys.length; i++) {
          legendData.push({ key: keys[i], color: colors[i % colors.length] });
        }
        break;
      }

      case 'multiple-line': {
        for (let i = 0; i < chartData.chart.data.length; i++) {
          const series = chartData.chart.data[i];
          // Use series.id as the stable unique key; pass label as the display text if available.
          legendData.push({
            key: series.id,
            label: series.label ?? series.id,
            color: colors[i % colors.length],
          });
        }
        break;
      }
      default:
        break;
    }

    return legendData;
  }, [chartData, colors]);

  // Estimate legend rows: ~4 items per row at typical widths, ~22px per row
  const legendRows = legend.length > 0 ? Math.ceil(legend.length / 4) : 0;
  const chartHeight = Math.max(180, 280 - legendRows * 24);

  return (
    <LegendCollapseToggleContext.Provider value={[legendOpen, setLegendOpen]}>
      <ChartContainer>
        <ChartContent style={{ height: chartHeight }}>{renderChart()}</ChartContent>
        {legend.length > 0 && <CompactChartLegend legend={legend} />}
      </ChartContainer>
    </LegendCollapseToggleContext.Provider>
  );

  function renderChart(): React.ReactElement | null {
    switch (chartData.type) {
      case 'bar':
      case 'stacked-bar':
      case 'pie':
        return renderSimpleBarOrPie(chartData, colors);
      case 'multiple-clustered-bar':
      case 'multiple-stacked-bar':
        return renderMultipleBar(chartData, colors);
      case 'line':
      case 'multiple-line':
        return <NivoLineChart chart={chartData.chart} colors={colors} />;
      case 'kpi-widget':
        return renderKPIWidget(chartData);
      case 'progress-bar-widget':
        return renderProgressBar(chartData, colors);
      case 'semi-circle-progress-widget':
        return renderSemiCircleProgress(chartData, colors);
      case 'data-table':
        return renderDataTable(chartData, colors);
      default:
        return null;
    }
  }
});

const ChartContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
});

const ChartContent = styled.div({
  minHeight: 0,
});

const DataTableContainer = styled.div({
  width: '100%',
  height: '100%',
  overflow: 'auto',
});
